import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/ui/design-system/components/ui/tooltip';
import { Badge } from '@/ui/design-system/components/ui/badge';
import { TbChartHistogram } from 'react-icons/tb';
import { lifeCycleFilterOptions } from 'utils/filters';

export const LIFE_CYCLE_STYLES = {
    early: {
        badge: 'bg-yellow-50 text-yellow-500 border-transparent',
        square: 'bg-yellow-500',
    },
    growth: {
        badge: 'bg-green-100 text-green-600 border-transparent',
        square: 'bg-green-600',
    },
    mainstream: {
        badge: 'bg-blue-50 text-blue-500 border-transparent',
        square: 'bg-blue-500',
    },
    fade: {
        badge: 'bg-red-50 text-red-600 border-transparent',
        square: 'bg-red-600',
    },
    none: {
        badge: 'bg-gray-100 text-gray-500 border-transparent',
        square: 'bg-gray-400',
    },
};

const NO_LIFE_CYCLE_LABEL = 'No Life Cycle';
export const NO_LIFE_CYCLE_DESCRIPTION = 'Mixed or volatile signals';

const LIFE_CYCLE_STAGES = new Set(['early', 'growth', 'mainstream', 'fade']);

export const LIFE_CYCLE_TOOLTIP_CONTENT_CLASSNAME =
    'bg-white text-foreground shadow-2xl rounded-xl p-0 w-[368px] pointer-events-none';

export function LifeCycleDefinitionsBody() {
    return (
        <>
            <div className="flex items-center gap-2 px-4 py-3 border-b border-neutral-200">
                <TbChartHistogram className="size-5 text-green-600" />
                <span className="text-sm font-medium">Trend life cycle</span>
            </div>
            <div className="flex flex-col gap-4 px-4 py-4">
                {lifeCycleFilterOptions.map((option) => {
                    const optionStyles = LIFE_CYCLE_STYLES[option.value];
                    return (
                        <div
                            key={option.value}
                            className="flex items-center gap-4"
                        >
                            <Badge
                                variant="outline"
                                className={`${optionStyles.badge} capitalize shrink-0 text-[10px]`}
                            >
                                <span
                                    className={`size-2 rounded-[2px] shrink-0 ${optionStyles.square}`}
                                />
                                {option.label}
                            </Badge>
                            <span className="text-xs text-muted-foreground leading-none">
                                {option.description}
                            </span>
                        </div>
                    );
                })}
                <div className="flex items-center gap-4 pt-3 border-t border-neutral-200">
                    <Badge
                        variant="outline"
                        className={`${LIFE_CYCLE_STYLES.none.badge} capitalize shrink-0 text-[10px]`}
                    >
                        <span
                            className={`size-2 rounded-[2px] shrink-0 ${LIFE_CYCLE_STYLES.none.square}`}
                        />
                        {NO_LIFE_CYCLE_LABEL}
                    </Badge>
                    <span className="text-xs text-muted-foreground leading-none">
                        {NO_LIFE_CYCLE_DESCRIPTION}
                    </span>
                </div>
            </div>
        </>
    );
}

export function LifeCycleBadge({ stage, mini = false }) {
    const isNone = !stage || !LIFE_CYCLE_STAGES.has(stage);
    const styles = isNone ? LIFE_CYCLE_STYLES.none : LIFE_CYCLE_STYLES[stage];
    const label = isNone ? NO_LIFE_CYCLE_LABEL : stage;
    return (
        <TooltipProvider delayDuration={300}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <Badge
                        variant="outline"
                        className={`${styles.badge} capitalize shrink-0 text-[10px] ${
                            mini ? 'px-1.5 py-0' : ''
                        }`}
                    >
                        <span
                            className={`${mini ? 'size-1.5' : 'size-2'} rounded-[2px] shrink-0 ${styles.square}`}
                        />
                        {label}
                    </Badge>
                </TooltipTrigger>
                <TooltipContent
                    className={LIFE_CYCLE_TOOLTIP_CONTENT_CLASSNAME}
                    sideOffset={8}
                >
                    <LifeCycleDefinitionsBody />
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}

export function LifeCycleRow({ data }) {
    const stage = data?.life_cycle;
    const option = lifeCycleFilterOptions.find((o) => o.value === stage);
    const description = option ? option.description : NO_LIFE_CYCLE_DESCRIPTION;
    return (
        <div className="flex items-center gap-2 flex-wrap">
            <LifeCycleBadge stage={stage} />
            <span className="text-sm text-muted-foreground">{description}</span>
        </div>
    );
}
